import {
  Button,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import React, { FC, useEffect, useMemo, useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { IPartnerContactFormValues } from "src/entities/partners/model/types";
import { useResetClientPersonMfaMutation } from "src/shared/api/partner";

interface IPersonalAccountAccessProps {
  hasPartnerProgramConsent?: boolean;
}

export const PersonalAccountAccess: FC<IPersonalAccountAccessProps> = ({
  hasPartnerProgramConsent,
}) => {
  const [resetClientPersonMfa, { isLoading }] =
    useResetClientPersonMfaMutation();

  const { control, setValue, getValues, clearErrors, watch } =
    useFormContext<IPartnerContactFormValues>();

  const [openWarning, setOpenWarning] = React.useState(false);

  const handleOpenWarning = () => setOpenWarning(true);
  const handleCloseWarning = () => setOpenWarning(false);

  const hasPcpAccess = watch("access_to_pcp_enabled");
  const isEnabled = watch("is_enabled");
  const contactId = watch("id");

  const initialPcpAccessRef = useRef<boolean>(
    Boolean(getValues("access_to_pcp_enabled")),
  );

  useEffect(() => {
    initialPcpAccessRef.current = Boolean(getValues("access_to_pcp_enabled"));
    clearErrors("disable_pcp_access_reason");
  }, [contactId, getValues, clearErrors]);

  useEffect(() => {
    if (isEnabled === false) {
      setValue("access_to_pcp_enabled", false, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [isEnabled, setValue]);

  useEffect(() => {
    if (hasPcpAccess === true) {
      setValue("disable_pcp_access_reason", undefined, {
        shouldDirty: true,
        shouldValidate: false,
      });
      clearErrors("disable_pcp_access_reason");
    }
  }, [hasPcpAccess, setValue, clearErrors]);

  const shouldRequireDisableReason = useMemo(() => {
    return initialPcpAccessRef.current === true && hasPcpAccess === false;
  }, [hasPcpAccess]);

  const handleReset2fa = async () => {
    const { id, partner_id } = getValues();

    try {
      await resetClientPersonMfa({
        partner: partner_id,
        partnerContactPerson: id,
      }).unwrap();

      enqueueSnackbar("Запрос на сброс двухфакторной аутентификации принят.", {
        variant: "success",
      });
      setOpenWarning(false);
    } catch (e) {
      enqueueSnackbar("Ошибка при сбросе двухфакторной аутентификации", {
        variant: "error",
      });
    }
  };

  return (
    <Stack spacing={4} alignItems="flex-start">
      <Controller
        control={control}
        name="access_to_pcp_enabled"
        render={({ field }) => (
          <FormControlLabel
            control={
              <Switch
                {...field}
                checked={!!field.value}
                disabled={!isEnabled}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            }
            label="Разрешен?"
          />
        )}
      />
      {hasPcpAccess === false && (
        <Controller
          control={control}
          name="disable_pcp_access_reason"
          rules={{
            validate: (value) => {
              if (!shouldRequireDisableReason) return true;

              return String(value ?? "").trim() ? true : "Это поле обязательно";
            },
            maxLength: {
              value: 500,
              message: "Максимум 500 символов",
            },
          }}
          render={({
            field: { ref, onChange, ...field },
            fieldState: { error },
          }) => (
            <TextField
              inputRef={ref}
              fullWidth
              {...field}
              label="Укажите причину"
              error={!!error}
              helperText={error?.message}
              multiline
              rows={3}
              onChange={(e) => {
                const v = e.target.value;
                onChange(v);

                if (String(v ?? "").trim()) {
                  clearErrors("disable_pcp_access_reason");
                }
              }}
              slotProps={{
                htmlInput: {
                  maxLength: 500,
                },
              }}
            />
          )}
        />
      )}

      <Controller
        control={control}
        name="access_to_pcp_finances_enabled"
        render={({ field }) => (
          <FormControlLabel
            control={<Switch {...field} checked={!!field.value} />}
            label="Доступ к блоку “Мои финансы и документы“"
          />
        )}
      />

      {hasPartnerProgramConsent !== undefined && (
        <FormControlLabel
          control={<Switch checked={hasPartnerProgramConsent} disabled />}
          label="Положение о партнерской программе принято"
        />
      )}

      <Typography variant="body2">
        Нажмите{" "}
        <Button
          variant="text"
          onClick={handleOpenWarning}
          sx={{
            p: 0,
            minWidth: "auto",
            textTransform: "none",
            textDecoration: "underline",
          }}
        >
          сюда
        </Button>
        , если пользователь запросил сброс канала двухфакторной аутентификации
        (2FA)
      </Typography>

      {openWarning && (
        <Paper
          elevation={0}
          sx={{
            bgcolor: "#FFF3CD",
            color: "text.primary",
            p: 3,
            borderRadius: 2,
          }}
        >
          <Stack spacing={2}>
            <Typography>
              Данное действие совершается только по просьбе самого пользователя.
            </Typography>

            <Typography>
              ВНИМАНИЕ! Прежде чем сбросить активный канал двухфакторной
              аутентификации, проведите процедуру проверки подлинности в ходе
              разговора с пользователем.
            </Typography>

            <Stack direction="row" spacing={2} mt={1}>
              <Button
                variant="outlined"
                onClick={handleCloseWarning}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  whiteSpace: "normal",
                  textAlign: "center",
                  fontSize: "12px",
                  lineHeight: 1,
                  py: 1,
                }}
              >
                Закрыть предупреждение без сброса канала 2FA
              </Button>

              <Button
                variant="contained"
                disabled={isLoading}
                onClick={handleReset2fa}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  whiteSpace: "normal",
                  textAlign: "center",
                  fontSize: "14px",
                  lineHeight: 1.3,
                  py: 1.2,
                }}
              >
                Сбросить канал 2FA
              </Button>
            </Stack>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
};
